import pluralize from 'pluralize'
import { type ReactNode, useContext, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { FillApi, FillApiContext, type FillFieldKey } from '@/models/fillApplication'
import { type RosterKind, useFillJump } from './FillJumpProvider'

/** Above this many rows a roster starts collapsed to its summary line. */
export const LARGE_FLEET_ROWS = 25

interface RosterFrameProps<T extends { id: string }> {
  kind: RosterKind
  /** The card's title, e.g. Drivers; the table shows it in its header row. */
  title: string
  noun: string
  rows: T[]
  isLoading: boolean
  isComplete: (row: T) => boolean
  /** Lets a check's field key jump here. */
  fieldKey?: FillFieldKey
  renderTable: (items: T[], setItems: (next: T[]) => void, header: RosterHeader) => ReactNode
}

/** The table's header row: the card's title, then what's incomplete and the large-fleet controls (or null). */
export interface RosterHeader {
  heading: string
  notice: ReactNode
}

export function RosterFrame<T extends { id: string }>({
  kind,
  title,
  noun,
  rows,
  isLoading,
  isComplete,
  fieldKey,
  renderTable,
}: RosterFrameProps<T>) {
  const { rosterRequest } = useFillJump()
  // The customer's review flags incomplete rows on the rows themselves, so the count and the filter are staff
  // tools there until the roster runs past a page and a flagged row can be out of sight.
  const customer = useContext(FillApiContext) === FillApi.CUSTOMER
  const [items, setItems] = useState(rows)
  const [expanded, setExpanded] = useState(false)
  const [onlyIncomplete, setOnlyIncomplete] = useState(false)

  useEffect(() => {
    setItems(rows)
  }, [rows])

  useEffect(() => {
    if (!rosterRequest?.kinds.includes(kind)) return
    setExpanded(true)
    setOnlyIncomplete(true)
  }, [rosterRequest, kind])

  const heading = <span className="text-ink text-md leading-none font-semibold">{title}</span>

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {heading}
        <SkeletonRows rows={3} />
      </div>
    )
  }

  const incomplete = items.filter((row) => !isComplete(row))
  const large = items.length > LARGE_FLEET_ROWS
  const showTable = !large || expanded
  const countIncomplete = (!customer || large) && incomplete.length > 0
  // A warning (a licence's format, say) jumps here too, with nothing incomplete to narrow to.
  const filtering = countIncomplete && onlyIncomplete

  // The tables hand back the rows they were given; with a filter on, that is only part of the roster.
  function mergeRows(next: T[]) {
    setItems((current) => {
      const nextById = new Map(next.map((row) => [row.id, row]))
      const known = new Set(current.map((row) => row.id))
      return [
        ...current.map((row) => nextById.get(row.id) ?? row),
        ...next.filter((row) => !known.has(row.id)),
      ]
    })
  }

  // A small roster's rows say how many there are; the line is for what needs doing or can't be seen.
  const notice =
    large || countIncomplete ? (
      <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="text-ink text-sm font-semibold">
          {large && pluralize(noun, items.length, true)}
          {large && countIncomplete && ', '}
          {countIncomplete && `${incomplete.length} incomplete`}
        </span>
        {large &&
          (expanded ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0"
              onClick={() => {
                setExpanded(false)
                setOnlyIncomplete(false)
              }}
            >
              Collapse
            </Button>
          ) : (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0"
              onClick={() => setExpanded(true)}
            >
              Show all
            </Button>
          ))}
        {countIncomplete && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={() => {
              setExpanded(true)
              setOnlyIncomplete((current) => !current)
            }}
          >
            {onlyIncomplete ? `Show every ${noun}` : 'Show incomplete'}
          </Button>
        )}
      </span>
    ) : null

  return (
    <div className="flex flex-col gap-3" data-fill-field={fieldKey}>
      {showTable ? (
        renderTable(filtering ? incomplete : items, mergeRows, { heading: title, notice })
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {heading}
          {notice}
        </div>
      )}
    </div>
  )
}
